import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserList } from "../../redux/actions/settingActions";
import * as Icon from "react-bootstrap-icons";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import moment from "moment";
import { subDays } from "date-fns";
import storeApis from "../../api/storeApi";
import MarkdownIt from "markdown-it";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";

const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
});

// Get start/end dates for a preset (used for initial state and applyPreset)
const getPresetRange = (preset) => {
  const now = new Date();
  let start = now;
  let end = now;
  if (preset === "today") {
    start = now;
    end = now;
  } else if (preset === "week") {
    start = subDays(now, 6); // inclusive 7 days
    end = now;
  } else if (preset === "month") {
    start = moment(now).subtract(1, "month").toDate();
    end = now;
  }
  return { startDate: start, endDate: end };
};

const DEFAULT_DATE_PRESET = "week";

const QueryAnalytics = (props) => {
  const dispatch = useDispatch();
  const authToken = props.authToken;
  const userList = useSelector((state) => state.setting.fetchUserList);
  // store selected user IDs as strings (backend expects ids in `user_emails` param name)
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAllUsersSelected, setIsAllUsersSelected] = useState(true);
  const [searchUser, setSearchUser] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDatePresetDropdown, setShowDatePresetDropdown] = useState(false);
  const [datePreset, setDatePreset] = useState(DEFAULT_DATE_PRESET); // today | week | month | custom
  const defaultRange = getPresetRange(DEFAULT_DATE_PRESET);
  const [dateRange, setDateRange] = useState(defaultRange);
  const [selectedDates, setSelectedDates] = useState([
    { ...defaultRange, key: "selection" },
  ]);
  const [expandedQueries, setExpandedQueries] = useState({});
  const presetDropdownRef = useRef(null);
  const dateFieldRef = useRef(null);
  const userDropdownRef = useRef(null);

  // API shape: [{ date, date_label, sessions: [{ id, user_email, user_first_name, user_last_name, queries: [...] }] }]
  const [apiGroups, setApiGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (authToken) {
      dispatch(fetchUserList(authToken, ""));
    }
  }, [authToken, dispatch]);

  // Initialize "All Users" selection when user list loads
  useEffect(() => {
    if (userList?.userList && isAllUsersSelected && selectedUsers.length === 0) {
      const allIds = userList.userList
        .map((u) => u.id)
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v));
      setSelectedUsers(allIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userList, isAllUsersSelected]);

  const fetchQueryAnalytics = async (exportFlag = false) => {
    if (!authToken) return null;
    setApiError(null);
    if (!exportFlag) setLoading(true);
    try {
      const startDateStr = moment(dateRange.startDate).isValid()
        ? moment(dateRange.startDate).format("YYYY-MM-DD")
        : undefined;
      const endDateStr = moment(dateRange.endDate).isValid()
        ? moment(dateRange.endDate).format("YYYY-MM-DD")
        : undefined;

      // Backend expects user IDs in `user_emails` param (historical naming).
      // Only send user_emails when specific users are selected (not "All Users").
      // Backend uses WHERE condition when user_emails is present, so omit it for "All Users".
      const userEmailsParam =
        !isAllUsersSelected && selectedUsers.length > 0 ? selectedUsers.join(",") : undefined;

      const params = {
        page,
        page_size: pageSize,
        // When exporting, backend expects `export=true` in query string
        export: exportFlag === true,
        // optional filters (backend may ignore if unsupported)
        start_date: startDateStr,
        end_date: endDateStr,
      };
      
      // Only add user_emails if specific users are selected
      if (userEmailsParam) {
        params.user_emails = userEmailsParam;
      }

      const response = await storeApis.get("admin/query-analytics", {
        params,
        headers: {
          Accept: exportFlag
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,*/*"
            : "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        ...(exportFlag ? { responseType: "blob" } : {}),
      });

      if (exportFlag) {
        return response;
      }

      // API sometimes returns { data: {...} } and sometimes returns {...} directly.
      // Also guard against rare cases where axios gives us a JSON string.
      const raw = response?.data;
      let parsed = raw;
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          // keep as-is; will fall back to empty arrays below
        }
      }
      const payload = parsed?.data || parsed;
      setApiGroups(payload?.queries || []);
      setTotalCount(payload?.total_count || 0);
      setTotalPages(payload?.total_pages || 1);
      return response;
    } catch (e) {
      setApiError(e?.response?.data?.message || "Failed to load query analytics");
      return null;
    } finally {
      if (!exportFlag) setLoading(false);
    }
  };

  // Fetch page data
  useEffect(() => {
    if (!authToken) return;
    setExpandedQueries({});
    fetchQueryAnalytics(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, page]);

  // Refetch when filters change (and reset to page 1)
  useEffect(() => {
    if (!authToken) return;
    if (page !== 1) {
      setPage(1);
      return;
    }
    setExpandedQueries({});
    fetchQueryAnalytics(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, dateRange.startDate, dateRange.endDate, isAllUsersSelected, selectedUsers]);

  // Auto-expand first query when API data loads
  useEffect(() => {
    if (apiGroups?.length > 0) {
      const firstGroup = apiGroups[0];
      const firstSession = firstGroup?.sessions?.[0];
      if (firstSession?.id) {
        setExpandedQueries((prev) => {
          if (Object.keys(prev).length === 0) return { [firstSession.id]: true };
          return prev;
        });
      }
    }
  }, [apiGroups]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(event.target)) {
        setShowDatePresetDropdown(false);
      }
      if (dateFieldRef.current && !dateFieldRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateRangeChange = (ranges) => {
    setSelectedDates([ranges.selection]);
  };

  const handleDateRangeApply = () => {
    const { startDate, endDate } = selectedDates[0];
    setDateRange({ startDate, endDate });
    setShowDatePicker(false);
    setDatePreset("custom");
    // Here you would filter the data based on date range
  };

  const applyPreset = (preset) => {
    const { startDate, endDate } = getPresetRange(preset);
    setDatePreset(preset);
    setDateRange({ startDate, endDate });
    setSelectedDates([{ startDate, endDate, key: "selection" }]);
    setShowDatePicker(false);
    setShowDatePresetDropdown(false);
  };

  const getPresetLabel = () => {
    switch (datePreset) {
      case "today":
        return "Today";
      case "week":
        return "1 Week";
      case "month":
        return "1 Month";
      default:
        return "Custom";
    }
  };

  const toggleQueryExpansion = (queryId) => {
    // Accordion behavior: only one open at a time
    setExpandedQueries((prev) => {
      const isCurrentlyOpen = !!prev?.[queryId];
      return isCurrentlyOpen ? {} : { [queryId]: true };
    });
  };

  const formatDate = (date) => {
    return moment(date).format("MMM D, YYYY");
  };

  const formatTime = (date) => {
    return moment(date).format("h:mm A");
  };

  const formatApiDateHeader = (group) => {
    const d = group?.date ? moment(group.date) : null;
    const label = group?.date_label || (d?.isValid() ? d.format("MMMM D, YYYY") : "");
    if (label === "Today" && d?.isValid()) return `Today, ${d.format("MMMM D")}`;
    if (label === "Yesterday" && d?.isValid()) return `Yesterday, ${d.format("MMMM D")}`;
    // label already can be like "January 13, 2026"
    return label;
  };

  const getInitialsFromNameOrEmail = (nameOrEmail) => {
    const s = String(nameOrEmail || "").trim();
    if (!s) return "U";
    // if email
    if (s.includes("@")) return s[0].toUpperCase();
    const parts = s.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return `${first}${second}`.toUpperCase() || first.toUpperCase();
  };

  const getDisplayUserName = (q) => {
    // session/user object
    const email = q?.user_email || q?.user_name;
    const first = q?.user_first_name;
    const last = q?.user_last_name;
    if (first || last) return `${first || ""} ${last || ""}`.trim();

    const match = userList?.userList?.find((u) => u?.email && u.email === email);
    if (match) return `${match.first_name} ${match.last_name}`.trim();
    return q?.user_name || email || "User";
  };

  const getFilteredGroups = () => {
    const groups = Array.isArray(apiGroups) ? apiGroups : [];
    // We rely on server-side filtering (start_date/end_date/user_emails).
    // Client-side filtering can cause false "No queries found" if mappings differ,
    // so only remove empty session groups.
    return groups.filter((g) => Array.isArray(g?.sessions) && g.sessions.length > 0);
  };

  const filteredGroups = getFilteredGroups();

  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const doc = new DOMParser().parseFromString(String(html), "text/html");
      return (doc.body.textContent || "").replace(/\s+\n/g, "\n").trim();
    } catch {
      return String(html).replace(/<[^>]*>/g, "").trim();
    }
  };

  const handleExportReport = () => {
    // Prefer backend export (export=true). If it returns JSON, fallback to CSV export.
    const downloadCsvFromCurrent = () => {
      const groups = filteredGroups;
      if (!groups.length) {
        // eslint-disable-next-line no-alert
        alert("No data to export.");
        return;
      }

      const rows = [];
      groups.forEach((g) => {
        (g?.sessions || []).forEach((s) => {
          const userName = getDisplayUserName(s);
          const email = s?.user_email || s?.user_name || "";
          (s?.queries || []).forEach((q) => {
            const createdAt = q?.timestamp || g?.date;
            rows.push({
              Date: g?.date || (moment(createdAt).isValid() ? moment(createdAt).format("YYYY-MM-DD") : ""),
              Time: q?.time || (moment(createdAt).isValid() ? formatTime(createdAt) : ""),
              "Session ID": s?.id || "",
              User: userName,
              Email: email,
              Question: q?.question || "",
              "AI Response": stripHtml(md.render(q?.answer || "")),
              "Conversation ID": q?.conversation_id || s?.id || "",
            });
          });
        });
      });

      const header = ["Date", "Time", "Session ID", "User", "Email", "Question", "AI Response", "Conversation ID"];
      const toCsvValue = (value) => {
        const s = String(value ?? "");
        const escaped = s.replace(/"/g, '""');
        if (/[",\n\r]/.test(escaped)) return `"${escaped}"`;
        return escaped;
      };

      const csvLines = [
        header.map(toCsvValue).join(","),
        ...rows.map((r) => header.map((h) => toCsvValue(r[h])).join(",")),
      ];
      const bom = "\uFEFF";
      const csv = bom + csvLines.join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      const start = moment(dateRange.startDate).isValid() ? moment(dateRange.startDate).format("YYYYMMDD") : "start";
      const end = moment(dateRange.endDate).isValid() ? moment(dateRange.endDate).format("YYYYMMDD") : "end";
      const filename = `query-analytics_${start}-${end}.csv`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };

    (async () => {
      const res = await fetchQueryAnalytics(true);
      if (!res) {
        downloadCsvFromCurrent();
        return;
      }

      const blob = res.data;
      const contentType = blob?.type || "";

      // If API returned JSON blob, fallback to CSV
      if (contentType.includes("application/json")) {
        downloadCsvFromCurrent();
        return;
      }

      // Try filename from header
      const disposition = res.headers?.["content-disposition"] || res.headers?.get?.("content-disposition") || "";
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      const filename = match ? match[1].replace(/['"]/g, "") : "query-analytics-export";

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    })();
  };

  const handleUserSelect = (userId) => {
    if (userId === "all") {
      // Toggle "All Users"
      if (isAllUsersSelected) {
        setIsAllUsersSelected(false);
        setSelectedUsers([]);
      } else {
        const allUserIds =
          userList?.userList
            ?.map((u) => u.id)
            .filter((v) => v !== undefined && v !== null)
            .map((v) => String(v)) || [];
        setIsAllUsersSelected(true);
        setSelectedUsers(allUserIds);
      }
    } else {
      const normalizedId = String(userId);
      // Toggle individual user
      const allUserIds =
        userList?.userList
          ?.map((u) => u.id)
          .filter((v) => v !== undefined && v !== null)
          .map((v) => String(v)) || [];
      setSelectedUsers((prev) => {
        let newSelected;
        if (prev.includes(normalizedId)) {
          newSelected = prev.filter((id) => id !== normalizedId);
        } else {
          newSelected = [...prev, normalizedId];
        }
        // If all users are selected, automatically check "All Users"
        if (newSelected.length === allUserIds.length && allUserIds.length > 0) {
          setIsAllUsersSelected(true);
        } else {
          setIsAllUsersSelected(false);
        }
        return newSelected;
      });
    }
    // Filter queries by user
  };

  // Get filtered users based on search
  const getFilteredUsers = () => {
    if (!userList?.userList) return [];
    if (!searchUser.trim()) return userList.userList;
    
    const searchLower = searchUser.toLowerCase();
    return userList.userList.filter(user => 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  };

  // Get display text for selected users
  const getSelectedUsersText = () => {
    if (isAllUsersSelected) {
      return "All Users";
    }
    if (selectedUsers.length === 0) {
      return "Select Users";
    }
    if (selectedUsers.length === 1) {
      const user = userList?.userList?.find((u) => String(u.id) === String(selectedUsers[0]));
      return user ? `${user.first_name} ${user.last_name}` : String(selectedUsers[0]);
    }
    return `${selectedUsers.length} Users Selected`;
  };

  return (
    <div className="flex flex-col h-full bg-white px-6 sm:px-8 lg:px-10 pb-6 sm:pb-10">
      {/* Title and Filters Section */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">Query Analytics</h1>
        
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4 mb-1 sm:mb-1">
          {/* Filter by User */}
          <div className="relative w-full sm:w-auto" ref={userDropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-medium"
            >
              <div className="flex items-center gap-2">
                <Icon.Person size={18} className="text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 truncate">{getSelectedUsersText()}</span>
              </div>
              <Icon.ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
            </button>
            
            {showUserDropdown && (
              <div className="absolute z-50 mt-2 left-0 right-0 sm:left-auto sm:right-auto sm:w-64 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-hidden flex flex-col">
                {/* Search Input */}
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <Icon.Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                {/* User List */}
                <div className="overflow-y-auto max-h-64">
                  <div className="py-1">
                    {/* All Users Option */}
                    <button
                      onClick={() => handleUserSelect("all")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={isAllUsersSelected}
                        onChange={() => handleUserSelect("all")}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="font-medium">All Users</span>
                    </button>

                    {/* Individual Users */}
                    {getFilteredUsers().map((user) => {
                      const isSelected = isAllUsersSelected || selectedUsers.includes(String(user.id));
                      return (
                        <button
                          key={user.id}
                          onClick={() => handleUserSelect(String(user.id))}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleUserSelect(String(user.id))}
                            className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="bg-amber-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs text-amber-600 font-semibold">
                              {getInitialsFromNameOrEmail(`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email)}
                            </span>
                          </div>
                          <span className="truncate">{user.first_name} {user.last_name}</span>
                        </button>
                      );
                    })}
                    
                    {getFilteredUsers().length === 0 && (
                      <div className="px-4 py-2 text-sm text-gray-500 text-center">
                        No users found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Date Range */}
          <div className="relative w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              {/* Preset dropdown */}
              <div className="relative w-full sm:w-44" ref={presetDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    // Preset dropdown should only open preset options (not the calendar)
                    setShowDatePresetDropdown((s) => !s);
                    setShowDatePicker(false);
                  }}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-medium"
                >
                  <span className="text-sm text-gray-700">{getPresetLabel()}</span>
                  <Icon.ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                </button>

                {showDatePresetDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                    {[
                      { key: "today", label: "Today" },
                      { key: "week", label: "1 Week" },
                      { key: "month", label: "1 Month" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => applyPreset(opt.key)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          datePreset === opt.key ? "text-orange-600 font-semibold" : "text-gray-700"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setDatePreset("custom");
                        setShowDatePresetDropdown(false);
                        setShowDatePicker(false); // calendar should open only via date field
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-t border-gray-200 ${
                        datePreset === "custom" ? "text-orange-600 font-semibold" : "text-gray-700"
                      }`}
                    >
                      Custom
                    </button>
                  </div>
                )}
              </div>

              {/* Date field (only visible for Custom; calendar opens only from here) */}
              {datePreset === "custom" && (
                <div className="relative w-full sm:w-auto" ref={dateFieldRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDatePresetDropdown(false);
                      setShowDatePicker(true);
                    }}
                    className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm font-medium hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon.CalendarDate size={18} className="text-gray-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
                      </span>
                    </div>
                  </button>

                  {showDatePicker && (
                    <div className="fixed sm:absolute z-50 mt-2 left-4 right-4 sm:left-0 sm:right-auto sm:w-auto bg-white border border-gray-200 rounded-lg shadow-xl max-w-[calc(100vw-2rem)] sm:max-w-none">
                      <div className="overflow-x-auto">
                        <DateRange
                          editableDateInputs={true}
                          onChange={handleDateRangeChange}
                          moveRangeOnFirstSelection={false}
                          ranges={selectedDates}
                          showMonthAndYearPickers={true}
                          showDateDisplay={false}
                        />
                      </div>
                      <div className="flex justify-end gap-2 p-3 border-t border-gray-200">
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDateRangeApply}
                          className="px-4 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600"
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Export Report Button */}
          <div className="w-full sm:w-auto sm:ml-auto">
            <button
              onClick={handleExportReport}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 text-sm font-medium shadow-sm hover:shadow transition-all"
            >
              <Icon.Download size={18} className="flex-shrink-0" />
              <span>Export Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* Query List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm sm:text-base">Loading...</p>
          </div>
        ) : apiError ? (
          <div className="text-center py-12 text-red-500">
            <p className="text-sm sm:text-base">{apiError}</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm sm:text-base">No queries found</p>
          </div>
        ) : (
          filteredGroups.map((group) => (
            <div key={group?.date || Math.random()} className="mb-8 sm:mb-10">
              {/* Date Header */}
              <div className="flex items-center mb-1 sm:mb-2">
                <Icon.CalendarDate size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium text-gray-600 whitespace-nowrap">
                  {formatApiDateHeader(group)}
                </span>
              </div>

              {/* divider line */}
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Query Items */}
              <div className="space-y-4 sm:space-y-5">
                {(group?.sessions || []).map((session) => {
                  const sessionId = session?.id;
                  const isExpanded = expandedQueries[sessionId];
                  const displayName = getDisplayUserName(session);
                  const email = session?.user_email || session?.user_name || "";
                  const sessionQueries = Array.isArray(session?.queries) ? session.queries : [];
                  const headerQuery = sessionQueries?.[0] || {};
                  const headerQuestion = headerQuery?.question || "";
                  const headerTime = headerQuery?.time || (headerQuery?.timestamp ? formatTime(headerQuery.timestamp) : "");

                  return (
                    <div
                      key={sessionId}
                      className="border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    >
                      {/* Query Header */}
                      <div
                        className="flex items-center justify-between cursor-pointer p-2 sm:p-3 bg-gray-50 gap-3 rounded-t-[8px]"
                        onClick={() => toggleQueryExpansion(sessionId)}
                      >
                         <div className="flex items-center gap-3 flex-1 min-w-0">
                           <div className="bg-amber-100 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                             <span className="text-sm text-amber-600 font-semibold">
                               {getInitialsFromNameOrEmail(displayName || email)}
                             </span>
                           </div>
                           <div className="flex items-center gap-2 flex-1 min-w-0">
                             <span className="font-semibold text-sm text-gray-900 whitespace-nowrap">
                               {displayName}
                             </span>
                             <div className="flex-1 flex justify-center min-w-0">
                               <span className="text-sm text-gray-600 text-center overflow-hidden text-ellipsis whitespace-nowrap block w-full">
                                 {headerQuestion.length > 100 ? `${headerQuestion.substring(0, 100)}...` : headerQuestion}
                               </span>
                             </div>
                           </div>
                         </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-500 whitespace-nowrap">{headerTime}</span>
                          {isExpanded ? (
                            <Icon.CaretUpFill size={16} className="text-gray-400" />
                          ) : (
                            <Icon.CaretDownFill size={16} className="text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="p-2 sm:p-3 border-t border-gray-200 space-y-4 bg-white rounded-b-[8px]">
                          {sessionQueries.length === 0 ? (
                            <div className="text-sm text-gray-500">No queries in this session.</div>
                          ) : (
                            sessionQueries.map((q, idx) => (
                              <div key={q?.id || idx} className={idx > 0 ? "pt-4 border-t border-gray-100" : ""}>
                                {/* User Question */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Icon.QuestionCircle size={16} className="text-gray-400 flex-shrink-0" />
                                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">USER QUESTION</h3>
                                  </div>
                                  <div className="text-xs text-gray-500">{q?.time || ""}</div>
                                </div>
                                <p className="text-sm text-gray-600 ml-6 leading-relaxed break-words">{q?.question}</p>

                                {/* AI Response */}
                                <div className="mt-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Icon.Cpu size={16} className="text-gray-400 flex-shrink-0" />
                                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">AI RESPONSE</h3>
                                  </div>
                                  <div
                                    className="text-sm text-gray-600 ml-6 prose prose-sm max-w-none break-words leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: md.render(q?.answer || "") }}
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Pagination (same style as Conversation) */}
        {totalPages > 1 && (
          <div className="flex my-4">
            <div className="me-auto">
              <ResponsivePagination current={page} total={totalPages} onPageChange={setPage} />
            </div>
            <div className="text-sm text-gray-500 my-auto">
              Total: {totalCount}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryAnalytics;
